import { useEffect, useState } from "react";
import "./App.css";
import styled from "styled-components";
import Dictionary from "./components/Dictionary";
import QuickEntry from "./components/QuickEntry/QuickEntry";
import BottomSection from "./components/BottomSection";
import BackgroundPanel from "./components/BackgroundPanel";
import RightPanel from "./components/RightPanel";
import DataLayerProvider, { useDataLayerContext } from "./components/ContextLayer/DataProvider/DataProvider";
import LightSwitchButton from "./components/Buttons/LightSwitchButton";
import AddButton from "./components/Buttons/AddButton";
import LanguageSwitch from "./components/LanguageSwitch/LanguageSwitch";
import { getDeviceID } from "./common";
import SettingsStorageProvider, { SettingsStorageType, useSettingsStorageContext } from "./components/ContextLayer/SettingsStorageProvider";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import withApiHost from "./hooks/useApiHost";
import WordPair from "./components/WordPair";
import { useRef } from "react";
import { useMediaQuery } from "@mui/material";
import MainPanel from "./components/MainPanel";
import QuickEntryModal from "./components/QuickEntry/QuickEntryModal";
import { useInitialFetcher } from "./hooks/engineHooks";
import TagSelector from "./components/TagSelector/TagSelector";
import { DictionaryEntryType, DictionaryType } from "./components/ContextLayer/DataProvider/types";
import { iterate, preCollectTags, sortDictionary, useDictinaryFilter } from "./components/ContextLayer/DataProvider/CommonDataMethods";
import TagSelectorModal from "./components/TagSelector/TagSelectorModal";

const UpperHalfAtom = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 55%;
  // background: rgba(0, 255, 0, 0.2);

  @media screen and (max-width: 1200px) {
    height: 62%;
  }
`

const TopBarAtom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 50px;
  // background: rgba(255, 0, 255, 0.2);
  box-sizing: border-box;
  padding: 0px 10px 0px 10px;
  margin-top: 5px;
`

export type DictionaryIteration = {
  iteration: number;
  currentDictionaryEntry: DictionaryEntryType | null;
}

function App() {
  const queryClient = new QueryClient();

  const [dataLayer, setDataLayer] = useState<DictionaryType>({
    dictionary: [],
    currentIteration: {
      currentDictionaryEntry: null,
      iteration: 0
    },
    tags: [],
    ready: false
  });

  const [ quickEntryModalVisible, setQuickEntryModalVisible ] = useState<boolean>(false);
  const [ tagSelectorModalVisible, setTagSelectorModalVisible ] = useState<boolean>(false);

  const currentStoredLanguage = localStorage.getItem("vocabularyTrainerMode");
  if(currentStoredLanguage === null) {
    localStorage.setItem('vocabularyTrainerMode', 'native');
  }

  const savedDarkModeState = localStorage.getItem("darkMode") === 'true';
  const [settings, setSettings] = useState<SettingsStorageType>({
    deviceID: getDeviceID(),
    currentLanguage: currentStoredLanguage === 'native' || currentStoredLanguage === null ? 'native' : 'foreign',
    darkMode: savedDarkModeState
  });

  const bottomSectionRef = useRef<HTMLDivElement>(null);
  const isWideScreen = useMediaQuery('screen and (orientation: landscape) and (min-width: 1200px)');

  return (
    <SettingsStorageProvider initialValue={{ settings, setSettings }}>
      <QueryClientProvider client={ queryClient }>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        <DataLayerProvider initialValue={{ dataLayer, setDataLayer }}>
          <DataUpdater/>
          <BackgroundPanel>
            <MainPanel>
              {!isWideScreen ? <QuickEntryModal visible={quickEntryModalVisible} closeHandler={() => setQuickEntryModalVisible(false)}/> : null}
              {!isWideScreen ? <TagSelectorModal visible={tagSelectorModalVisible} closeHandler={() => setTagSelectorModalVisible(false)}/> : null}
              <UpperHalfAtom>
                <TopBarAtom>
                  <LanguageSwitch/>
                  <LightSwitchButton/>
                  <AddButton onClick={() => setQuickEntryModalVisible(!quickEntryModalVisible)} opaque={quickEntryModalVisible}/>
                </TopBarAtom>

                <WordPair/>
              </UpperHalfAtom>

              <BottomSection ref={bottomSectionRef} tagsButtonAction={() => setTagSelectorModalVisible(true)}>
                {/* <GlassFrame>
                  <IncorrectButton onClick={fadeAndStartBeating} onComplete={stopBeating}/>
                  <NextButton onClick={fadeAndStartBeating} onComplete={stopBeating}/>
                </GlassFrame> */}
                {/* {!isWideScreen ? <TagsButton onClick={() => setTagSelectorModalVisible(true)}/> : null} */}
              </BottomSection>
            </MainPanel>

            <RightPanel>
              <QuickEntry />
              <TagSelector/>
              <Dictionary />
            </RightPanel>
          </BackgroundPanel>
        </DataLayerProvider>
      </QueryClientProvider>
    </SettingsStorageProvider>
  );
}

function DataUpdater() {
  const { dataLayer, setDataLayer } = useDataLayerContext();
  const { settings } = useSettingsStorageContext();
  const apiHost = withApiHost();
  const filterDictonary = useDictinaryFilter();

  const { isPending, isError, error, data } = useInitialFetcher(apiHost, settings.currentLanguage, settings.deviceID);

  useEffect(() => {
    if (data && data.ready) {
      const tags = preCollectTags(data);
      let filteredDictionary = filterDictonary(data.dictionary);
      const sortedDictionary = sortDictionary(filteredDictionary);
      const nextIteration = iterate(sortedDictionary, 0);

      const aggregationData: DictionaryType = {
        dictionary: data.dictionary,
        currentIteration: nextIteration,
        tags: tags,
        ready: true
      }
      setDataLayer(aggregationData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setDataLayer, settings.deviceID, settings.currentLanguage]);

  useEffect(() => {
    // console.log('DataUpdater: tags updated');
    let filteredDictionary = filterDictonary(dataLayer.dictionary);
    const sortedDictionary = sortDictionary(filteredDictionary);
    const nextIteration = iterate(sortedDictionary, 0);

    setDataLayer({
      ...dataLayer,
      currentIteration: nextIteration
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLayer?.tags])

  if(isPending) return <></>
  
  if(isError) {
    console.error("Error fetching dictionary data:", error);
    return <></>;
  }

  return <></>
}

export default App;